import OrgObjective from 'models/orgObjective';

import { getGoalStatusColor } from 'constants/ProjectConstants';

export const NODE_HEIGHT = 144;
export const GOALS_NODE_HEIGHT = 252;
export const NODE_WIDTH = 332;
export const ROOT_NODE_WIDTH = 300;

const createEdgeObject = ({ parentNodeId, childNodeId }) => ({
  id: `${parentNodeId}-${childNodeId}`,
  from: parentNodeId,
  to: childNodeId,
  fromPort: `${parentNodeId}-to`,
  toPort: `${childNodeId}-from`,
});

export const toggleNodeGoalsView = ({ nodeId, nodesMap }) => {
  const node = nodesMap[nodeId];
  if (!node) {
    console.error(`Node with node id ${nodeId} not found in nodesMap`);
    return;
  }
  if (node.data.isGoalsExpanded) {
    node.height = NODE_HEIGHT;
    node.data.isGoalsExpanded = false;
  } else {
    node.height = GOALS_NODE_HEIGHT;
    node.data.isGoalsExpanded = true;
  }
};

export const insertNewObjective = ({ newObjective, nodesMap, idTree, nodeIdPathArray: parentNodeIdPathArray }) => {
  const newTreeNode = createNewObjectiveNode({
    objective: newObjective,
    parentNodeIdPathArray,
  });
  nodesMap[newObjective.id] = newTreeNode;
  // update that parent node now has children
  if (parentNodeIdPathArray.length > 1) {
    // because root node is already always expanded
    const [parentNodeId] = parentNodeIdPathArray.slice(-1); // gets last item in array
    nodesMap[parentNodeId].data.hasChildNodes = true;
  }
  insertInIdTree({ newObjectiveId: newObjective.id, idTree, parentNodeIdPathArray });
};

const insertInIdTree = ({ newObjectiveId, idTree, parentNodeIdPathArray }) => {
  let pointer = idTree;
  let i = 0;
  for (i; i < parentNodeIdPathArray.length; i++) {
    pointer = pointer[parentNodeIdPathArray[i]];
  }
  pointer[newObjectiveId] = {};
};

export const getNodesEdgesForTree = ({ nodeIdPathArray = ['root'], idTree, nodesMap }) => {
  const nodes = [];
  const edges = [];

  // revert all nodes heights to normal
  Object.keys(nodesMap).forEach((id) => {
    nodesMap[id].height = NODE_HEIGHT;
    nodesMap[id].data.isGoalsExpanded = false;
  });

  // this pointer will move with path nodes, this is basically used to get all children for a node when needed
  let currentNodeIdTreePointer = idTree;

  nodeIdPathArray.forEach((nodeId, nodeIdIdx) => {
    const currentNode = nodesMap[nodeId];
    currentNodeIdTreePointer = currentNodeIdTreePointer[nodeId];
    // console.log(currentNodeIdTreePointer, nodeId);
    nodes.push(nodesMap[nodeId]);
    // if it's last node in nodeIdPathArray, also add all children nodes and edges to them
    // console.dir(Object.keys(currentNodeIdTreePointer.children));
    if (nodeIdIdx === nodeIdPathArray.length - 1) {
      if (nodeId !== 'root' && nodesMap[nodeId].goalsCount > 0) {
        nodesMap[nodeId].data.isGoalsExpanded = true;
        nodesMap[nodeId].height = GOALS_NODE_HEIGHT;
      }
      Object.keys(currentNodeIdTreePointer).forEach((id) => {
        nodes.push(nodesMap[id]);
        edges.push(createEdgeObject({ parentNodeId: currentNode.id, childNodeId: id }));
      });
    } else {
      // add edge to next intermediate node in path
      edges.push(
        createEdgeObject({
          parentNodeId: currentNode.id,
          childNodeId: nodeIdPathArray[nodeIdIdx + 1],
        })
      );
    }
  });
  return { nodes, edges };
};

const createNewObjectiveNode = ({ objective, parentNodeIdPathArray }) => {
  const processedNode = {
    data: {
      // ########################
      goalsCount: 0,
      goalsAggregatePercentage: null,
      goalEmployeesCount: 0,
      // ########################
      goalMembers: [],
      // goalMembers: [
      //   {
      //     id: 1234,
      //     displayName: 'joseph',
      //     profilePhoto:
      //       'https://media.mesh.ai/1064/media/profilePhoto/9296/2021/02/15/pp.jpeg_1613368810924'
      //   },
      //   {
      //     id: 4567,
      //     displayName: 'joseph',
      //     profilePhoto:
      //       'https://media.mesh.ai/1064/media/profilePhoto/9296/2021/02/15/pp.jpeg_1613368810924'
      //   }
      // ],
      objective,
    },
  };
  processedNode.data.nodeIdPathArray = [...parentNodeIdPathArray, objective.id];
  processedNode.data.hasChildNodes = false;
  processedNode.data.statusColor = getGoalStatusColor(null);
  processedNode.data.isGoalsExpanded = false;
  // objective id is tree node id also
  processedNode.id = objective.id;

  processedNode.height = NODE_HEIGHT;
  processedNode.width = NODE_WIDTH;
  processedNode.ports = [
    {
      id: `${objective.id}-to`,
      side: 'EAST',
    },
    {
      id: `${objective.id}-from`,
      side: 'WEST',
    },
  ];
  return processedNode;
};

export const processTreeAPIData = (rootObjectives) => {
  // return res.entity.map(objective => OrgObjective.fromJSON(objective));

  // this hierarchial id tree will be used to regenerate tree when it is modified,
  // such as addition of goal, objectives, or expanding/collapsing some node.
  const idTree = {
    root: {},
  };

  const rootNode = {
    id: 'root',
    height: NODE_HEIGHT,
    width: ROOT_NODE_WIDTH,
    ports: [
      {
        id: 'root-to',
        side: 'EAST',
      },
    ],
    data: {
      nodeIdPathArray: ['root'],
    },
    // text: 'Objectives'
  };

  // 'reaflow' library used for objective tree takes the graph(tree in our case) as an array of nodes and edgelist
  const nodesMap = { root: rootNode };
  // will have elements like: {id:'xid-yid', from:'parentId', to:'childId'}

  const traverseTree = (treeNode, parentIdTreeNode, nodeIdPathArray = []) => {
    const { objective, subObjectives, aggregateGoalProgressLevel, ...rest } = treeNode;
    // this is objectiveId only for each node, except for the root node
    const objectiveId = objective.organizationTag.uuid;

    // make entry in idTree
    parentIdTreeNode[objectiveId] = {};

    // prepare processed node for nodes array
    const processedNode = {
      data: {
        ...rest,
        // remove these ############
        // goalsCount: 4,
        // goalMembers: [
        //   {
        //     id: 1234,
        //     displayName: 'joseph',
        //     profilePhoto:
        //       'https://media.mesh.ai/1064/media/profilePhoto/9296/2021/02/15/pp.jpeg_1613368810924'
        //   },
        //   {
        //     id: 4567,
        //     displayName: 'joseph',
        //     profilePhoto:
        //       'https://media.mesh.ai/1064/media/profilePhoto/9296/2021/02/15/pp.jpeg_1613368810924'
        //   }
        // ]
      },
    };
    processedNode.data.objective = OrgObjective.fromJSON(treeNode.objective);
    processedNode.data.nodeIdPathArray = [...nodeIdPathArray, objectiveId];
    processedNode.data.hasChildNodes = subObjectives.length !== 0;
    processedNode.data.statusColor = getGoalStatusColor(aggregateGoalProgressLevel);
    processedNode.data.isGoalsExpanded = false;
    // objective id is tree node id also
    processedNode.id = objectiveId;

    processedNode.height = NODE_HEIGHT;
    processedNode.width = NODE_WIDTH;
    processedNode.ports = [
      {
        id: `${objectiveId}-to`,
        side: 'EAST',
      },
      {
        id: `${objectiveId}-from`,
        side: 'WEST',
      },
    ];
    nodesMap[objectiveId] = processedNode;

    // recursively traverse for child objectives
    subObjectives.forEach((node) =>
      traverseTree(node, parentIdTreeNode[objectiveId], [...processedNode.data.nodeIdPathArray])
    );
  };

  rootObjectives.forEach((firstLevelNode) => traverseTree(firstLevelNode, idTree.root, rootNode.data.nodeIdPathArray));
  return { nodesMap, idTree };
};
